// Selectors
import {getIconById} from '~/selectors/CanvasSelectors';
// Action Types
import AnalyticsTypes from '~/constants/types/AnalyticsTypes';
import IconTypes from '~/constants/types/IconTypes';
import TextTypes from '~/constants/types/TextTypes';
// Utils
import {normalizeTagName} from '~/utils/analytics';
import {createCanvasRemoveEvents, hasTextIcon, isTextIcon} from './utils';
import {isText} from '~/data/accessors/icon';

const Text = {
  start: (_, {payload}) => {
    const {source} = payload;
    if (!source) return null;
    return {
      event: 'lab_text_start',
      source: source || ''
    };
  },
  add: (_, {payload}) => {
    const {icon} = payload;
    if (!icon?.text) return null;
    return {
      event: 'lab_text_add'
    };
  },
  editTools: () => {
    return {
      event: 'lab_text_editTools'
    };
  },
  browse: (_, {payload}) => {
    const {fontCategory} = payload;
    return {
      event: 'lab_text_font_browse',
      text_font_category: normalizeTagName(fontCategory)
    };
  },
  browseCategories: (_, {payload}) => {
    const {fontCategory} = payload;
    return {
      event: 'lab_text_font_browse',
      text_font_category: normalizeTagName(fontCategory)
    };
  },
  selected: (_, {payload}) => {
    const {fontCategory, fontName} = payload;
    return {
      event: 'lab_text_font_selected',
      text_font_category: normalizeTagName(fontCategory),
      text_font_name: normalizeTagName(fontName)
    };
  },
  search: (_, {payload}) => {
    const {searchTerm, hasResults} = payload;
    return {
      event: 'lab_text_search',
      text_font_search_term: searchTerm,
      text_has_results: hasResults
    };
  },
  remove: (_, {payload}) => {
    return createCanvasRemoveEvents(payload, {
      targetIconType: 'TEXT'
    });
  },
  editToolsColor: (state, {payload}) => {
    const {color, iconId, source} = payload;
    if (!source || !isTextIcon({getIconById, state, iconId})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'text',
      tool_name: 'color',
      tool_value: color?.name ? normalizeTagName(color?.name) : null,
      tool_source: source
    };
  },
  editToolsOutline: (_, {payload}) => {
    const {selectedIcon, source} = payload;
    if (!source || !selectedIcon) return null;
    const color = selectedIcon.outlineColor?.name
      ? normalizeTagName(selectedIcon.outlineColor?.name)
      : null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'text',
      tool_name: 'outline',
      tool_value: color,
      tool_source: source
    };
  },
  editToolsOutlineComplete: (_, {payload}) => {
    const {selectedIcon, source} = payload;
    if (!source) return null;
    const thickness = selectedIcon?.outlineWidth;
    const color = selectedIcon.outlineColor?.name
      ? normalizeTagName(selectedIcon.outlineColor?.name)
      : null;
    return [
      {
        event: 'lab_edit_tools',
        tool_icon_type: 'text',
        tool_name: 'outline_thickness',
        tool_value: thickness,
        tool_source: source
      },
      {
        event: 'lab_edit_tools',
        tool_icon_type: 'text',
        tool_name: 'outline_color',
        tool_value: color,
        tool_source: source
      }
    ];
  },
  editToolsShape: (_, {payload}) => {
    const {selectedIcon, source} = payload;
    if (!source) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'text',
      tool_name: 'text_shape',
      tool_value: selectedIcon?.effectName,
      tool_source: source
    };
  },
  editToolsSizeMenuOption: (_, {payload}) => {
    const {iconType} = payload;
    if (!isText(iconType)) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'text',
      tool_name: 'resize',
      tool_value: null,
      tool_source: 'panel'
    };
  },
  editToolsSizeDrag: (_, {payload}) => {
    const {transforms} = payload;
    if (!hasTextIcon({transforms})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'text',
      tool_name: 'resize',
      tool_value: null,
      tool_source: 'canvas'
    };
  },
  editToolsRotation: (state, {payload}) => {
    const {iconId, source} = payload;
    if (!source || !isTextIcon({getIconById, state, iconId})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'text',
      tool_name: 'rotate',
      tool_value: null,
      tool_source: source
    };
  },
  editToolsPositionCenter: (state, {payload}) => {
    const {source, iconIds} = payload;
    if (!source || !hasTextIcon({iconIds, getIconById, state})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'text',
      tool_name: 'center',
      tool_value: null,
      tool_source: source
    };
  },
  editToolsLayerForward: (state, {payload}) => {
    const {source, iconIds} = payload;
    if (!source || !hasTextIcon({iconIds, getIconById, state})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'text',
      tool_name: 'layer_forward',
      tool_value: null,
      tool_source: source
    };
  },
  editToolsLayerBackward: (state, {payload}) => {
    const {source, iconIds} = payload;
    if (!source || !hasTextIcon({iconIds, getIconById, state})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'text',
      tool_name: 'layer_backward',
      tool_value: null,
      tool_source: source
    };
  },
  editToolsAlignment: (state, {payload}) => {
    const {align, iconId, source} = payload;
    if (!source || !isTextIcon({getIconById, state, iconId})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'text',
      tool_name: 'alignment',
      tool_value: align,
      tool_source: source
    };
  }
};

const TextEvents = {
  [AnalyticsTypes.TEXT_START]: Text.start,
  [TextTypes.ADD_TEXT]: Text.add,
  [AnalyticsTypes.TEXT_EDIT]: Text.editTools,
  [AnalyticsTypes.TEXT_FONT_BROWSE]: Text.browse,
  [AnalyticsTypes.TEXT_FONT_BROWSE_CATEGORIES]: Text.browseCategories,
  [TextTypes.UPDATE_FONT]: Text.selected,
  [AnalyticsTypes.TEXT_SEARCH]: Text.search,
  [IconTypes.REMOVE]: Text.remove,
  [IconTypes.CHANGE_COLOR]: Text.editToolsColor,
  [AnalyticsTypes.CHANGE_TEXT_OUTLINE]: Text.editToolsOutline,
  [AnalyticsTypes.CHANGE_TEXT_OUTLINE_COMPLETE]: Text.editToolsOutlineComplete,
  [AnalyticsTypes.CHANGE_TEXT_EFFECT]: Text.editToolsShape,
  [TextTypes.UPDATE_FONT_HEIGHT]: Text.editToolsSizeMenuOption,
  [IconTypes.RESIZE]: Text.editToolsSizeDrag,
  [IconTypes.ROTATE]: Text.editToolsRotation,
  [IconTypes.CENTER]: Text.editToolsPositionCenter,
  [IconTypes.FORWARD]: Text.editToolsLayerForward,
  [IconTypes.BACKWARD]: Text.editToolsLayerBackward,
  [IconTypes.ALIGN]: Text.editToolsAlignment
};

export default TextEvents;
